import { useState } from 'react';
import type { ArtistData, GenreAttribute } from '../types';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';
import type { FavoriteArtistType } from '../../../components/feature/FavoriteButton';

interface ArtistHeroProps {
  artist: ArtistData;
  accentColor: string;
  genresAndAttributes: GenreAttribute[];
  favoriteCount: number;
  countLoading: boolean;
  /** アーティストID（FavoriteButton用） */
  artistId?: string;
  /** アーティストタイプ（FavoriteButton用） */
  artistType?: FavoriteArtistType;
  /** モバイル用: スクロールに応じた背景透明度 */
  bgOpacity?: number;
  /** デスクトップ用レイアウトかどうか */
  desktop?: boolean;
  /** 収益化リンク */
  supportUrl?: string;
  storeUrl?: string;
  /** プレミアム案内バナーを表示するか（非プレミアムMAKER本人のみ） */
  showPremiumBanner?: boolean;
  /** トラッキングコールバック */
  onResonateClick?: () => void;
  onFragmentClick?: () => void;
  /** 🌟 個別レベル関連のProps */
  fanLevel?: number;
  currentExp?: number;
  nextLevelExp?: number;
  checkInExp?: number;
  supportExp?: number;
  hasVeteranBonus?: boolean;
}

function GenreTags({ items, accentColor }: { items: GenreAttribute[]; accentColor: string }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {items.map((item, index) => (
        <div
          key={index}
          className="px-3 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm transition-all duration-300 cursor-default"
          style={{
            background: `${item.color || accentColor}15`,
            borderColor: `${item.color || accentColor}40`,
            color: item.color || accentColor,
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            el.style.background = `${item.color || accentColor}28`;
            el.style.borderColor = `${item.color || accentColor}90`;
            el.style.boxShadow = `0 0 12px ${item.color || accentColor}50, inset 0 0 8px ${item.color || accentColor}15`;
            el.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            el.style.background = `${item.color || accentColor}15`;
            el.style.borderColor = `${item.color || accentColor}40`;
            el.style.boxShadow = 'none';
            el.style.transform = 'scale(1)';
          }}
        >
          {item.name}
        </div>
      ))}
    </div>
  );
}

/** 🌟 お気に入り登録数 + お気に入りボタンの統合コンポーネント */
function FavoriteSection({
  count,
  accentColor,
  artistId,
  artistType,
}: {
  count: number;
  accentColor: string;
  artistId?: string;
  artistType?: FavoriteArtistType;
}) {
  return (
    <div className="flex items-center gap-3 mb-6">
      {/* お気に入り登録数（半分の幅） */}
      <div
        className="flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border flex-1"
        style={{
          background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}10)`,
          borderColor: `${accentColor}40`,
        }}
      >
        <i className="ri-heart-fill text-sm" style={{ color: accentColor }} />
        <span className="text-white font-bold text-sm">{count}</span>
        <span className="text-gray-400 text-xs whitespace-nowrap">お気に入り</span>
      </div>

      {/* お気に入り登録ボタン（半分の幅） */}
      <div
        className="flex items-center justify-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border flex-1"
        style={{
          background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`,
          borderColor: `${accentColor}60`,
        }}
      >
        {artistId ? (
          <>
            <FavoriteButton
              artistId={artistId}
              artistType={artistType || 'artist'}
              size="sm"
            />
            <span className="text-sm font-bold whitespace-nowrap" style={{ color: accentColor }}>
              登録
            </span>
          </>
        ) : (
          <>
            <i className="ri-star-line text-base" style={{ color: accentColor }} />
            <span className="text-sm font-bold whitespace-nowrap" style={{ color: accentColor }}>登録</span>
          </>
        )}
      </div>
    </div>
  );
}

/** 🌟 DEPTH LV ポップアップコンポーネント */
function DepthLevelPopup({
  fanLevel = 2,
  currentExp = 26,
  nextLevelExp = 30,
  checkInExp = 15,
  supportExp = 11,
  hasVeteranBonus = false,
  accentColor,
  onClose,
}: {
  fanLevel: number;
  currentExp: number;
  nextLevelExp: number;
  checkInExp: number;
  supportExp: number;
  hasVeteranBonus: boolean;
  accentColor: string;
  onClose: () => void;
}) {
  const remainingExp = nextLevelExp - currentExp;
  const checkInCount = Math.floor(checkInExp / 5);
  const supportAmount = supportExp * 1000;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative bg-black/95 backdrop-blur-xl rounded-2xl p-6 max-w-md w-full border-2 shadow-2xl"
        style={{
          borderColor: accentColor,
          boxShadow: `0 0 40px ${accentColor}60, 0 0 80px ${accentColor}30, inset 0 0 30px ${accentColor}10`,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* 閉じるボタン */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 cursor-pointer"
          style={{
            background: `${accentColor}20`,
            color: accentColor,
          }}
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* タイトル */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
            />
            <p className="text-gray-500 text-xs tracking-widest uppercase">YOUR DEPTH</p>
          </div>
          <h3
            className="font-mono text-3xl font-bold tracking-wider"
            style={{
              color: accentColor,
              textShadow: `0 0 20px ${accentColor}80`,
            }}
          >
            DEPTH LV. {fanLevel}
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            次のレベルまであと <span className="font-mono font-bold" style={{ color: accentColor }}>{remainingExp} EXP</span>
          </p>
        </div>

        {/* プログレスバー */}
        <div className="mb-6">
          <div
            className="h-2 rounded-full overflow-hidden"
            style={{ background: `${accentColor}20` }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(currentExp / nextLevelExp) * 100}%`,
                background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80)`,
                boxShadow: `0 0 10px ${accentColor}`,
              }}
            />
          </div>
        </div>

        {/* EXP詳細 */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">📊 現在の獲得EXP</span>
            <span className="font-mono font-bold text-white">{currentExp} EXP</span>
          </div>
          <div className="pl-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">・現場チェックイン</span>
              <span className="font-mono text-gray-300">{checkInExp} EXP ({checkInCount}回)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">・直接支援</span>
              <span className="font-mono text-gray-300">{supportExp} EXP ({supportAmount.toLocaleString()}円)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">・古参ボーナス</span>
              <span className="font-mono text-gray-300">{hasVeteranBonus ? 'あり' : 'なし'}</span>
            </div>
          </div>
        </div>

        {/* アクションボタン */}
        <div>
          <p className="text-gray-400 text-sm mb-3">💡 レベルを上げるには？</p>
          <div className="flex flex-wrap gap-3">
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{
                background: `${accentColor}15`,
                border: `1px solid ${accentColor}40`,
                color: accentColor,
              }}
            >
              <i className="ri-calendar-event-line" />
              ライブに行く
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{
                background: `${accentColor}15`,
                border: `1px solid ${accentColor}40`,
                color: accentColor,
              }}
            >
              <i className="ri-sparkling-2-line" />
              投げ銭する
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{
                background: `${accentColor}15`,
                border: `1px solid ${accentColor}40`,
                color: accentColor,
              }}
            >
              <i className="ri-shopping-bag-line" />
              グッズを買う
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 還元・グッズ・Resonateボタン */
function RevenueButtons({
  supportUrl,
  storeUrl,
  accentColor,
  onResonateClick,
  onFragmentClick,
}: {
  supportUrl?: string;
  storeUrl?: string;
  accentColor: string;
  onResonateClick?: () => void;
  onFragmentClick?: () => void;
}) {
  const secondColor = accentColor === '#00ff41' ? '#ff006e' : '#00ff41';
  const resonateColor = '#f5c842';

  const handleResonateClick = () => {
    if (onResonateClick) {
      try {
        onResonateClick();
      } catch (err) {
        console.error('Tracking error (resonate):', err);
      }
    }
  };

  const handleFragmentClick = () => {
    if (onFragmentClick) {
      try {
        onFragmentClick();
      } catch (err) {
        console.error('Tracking error (fragment):', err);
      }
    }
  };

  return (
    <div className="flex flex-wrap gap-3">
      {/* Resonateボタン：常に表示 */}
      <button
        onClick={handleResonateClick}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-widest uppercase transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
        style={{
          background: `linear-gradient(135deg, ${resonateColor}25, ${resonateColor}08)`,
          border: `1px solid ${resonateColor}90`,
          color: resonateColor,
          boxShadow: `0 0 18px ${resonateColor}60, 0 0 40px ${resonateColor}25, inset 0 0 12px ${resonateColor}12`,
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            `0 0 30px ${resonateColor}90, 0 0 70px ${resonateColor}45, inset 0 0 20px ${resonateColor}22`;
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.boxShadow =
            `0 0 18px ${resonateColor}60, 0 0 40px ${resonateColor}25, inset 0 0 12px ${resonateColor}12`;
        }}
      >
        <i className="ri-sparkling-2-line text-base" />
        RESONATE
      </button>

      {supportUrl && (
        <a
          href={supportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-widest uppercase transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          style={{
            background: `linear-gradient(135deg, ${accentColor}20, ${accentColor}08)`,
            border: `1px solid ${accentColor}80`,
            color: accentColor,
            boxShadow: `0 0 18px ${accentColor}50, 0 0 40px ${accentColor}20, inset 0 0 12px ${accentColor}10`,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow =
              `0 0 28px ${accentColor}80, 0 0 60px ${accentColor}40, inset 0 0 20px ${accentColor}20`;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow =
              `0 0 18px ${accentColor}50, 0 0 40px ${accentColor}20, inset 0 0 12px ${accentColor}10`;
          }}
        >
          <i className="ri-hand-coin-line text-base" />
          還元
        </a>
      )}
      {storeUrl && (
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleFragmentClick}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-widest uppercase transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          style={{
            background: `linear-gradient(135deg, ${secondColor}20, ${secondColor}08)`,
            border: `1px solid ${secondColor}80`,
            color: secondColor,
            boxShadow: `0 0 18px ${secondColor}50, 0 0 40px ${secondColor}20, inset 0 0 12px ${secondColor}10`,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow =
              `0 0 28px ${secondColor}80, 0 0 60px ${secondColor}40, inset 0 0 20px ${secondColor}20`;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.boxShadow =
              `0 0 18px ${secondColor}50, 0 0 40px ${secondColor}20, inset 0 0 12px ${secondColor}10`;
          }}
        >
          <i className="ri-store-2-line text-base" />
          グッズ
        </a>
      )}
    </div>
  );
}

/** プレミアム案内バナー（非プレミアムMAKER向け） */
function PremiumHintBanner() {
  return (
    <a
      href="/layer-edit"
      className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-xs tracking-wider transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap mb-6 group"
      style={{
        background: 'rgba(255,0,110,0.06)',
        border: '1px solid rgba(255,0,110,0.25)',
        color: 'rgba(255,255,255,0.5)',
      }}
    >
      <i className="ri-vip-crown-line text-sm group-hover:text-neon-pink transition-colors" style={{ color: 'rgba(255,0,110,0.6)' }} />
      <span>
        <span style={{ color: 'rgba(255,0,110,0.8)' }}>還元・グッズ</span>
        ボタンを表示するには
        <span className="ml-1 font-bold" style={{ color: 'rgba(255,0,110,0.9)' }}>PREMIUM</span>
        へ
      </span>
      <i className="ri-arrow-right-s-line text-sm opacity-50 group-hover:opacity-100 transition-opacity" />
    </a>
  );
}

/** モバイル用ヒーロー（フルスクリーン背景 + スクロールフェード） */
export function ArtistHeroMobile({
  artist,
  accentColor,
  genresAndAttributes,
  favoriteCount,
  countLoading,
  artistId,
  artistType,
  bgOpacity = 1,
  supportUrl,
  storeUrl,
  showPremiumBanner,
  onResonateClick,
  onFragmentClick,
  fanLevel = 2,
  currentExp = 26,
  nextLevelExp = 30,
  checkInExp = 15,
  supportExp = 11,
  hasVeteranBonus = false,
}: ArtistHeroProps) {
  const [showDepthPopup, setShowDepthPopup] = useState(false);

  const handleOpenDepthPopup = () => {
    setTimeout(() => setShowDepthPopup(true), 50);
  };

  return (
    <>
      {/* Fixed Full-Screen Background Image */}
      <div
        className="fixed inset-0 z-0 lg:hidden transition-opacity duration-100"
        style={{ opacity: bgOpacity }}
      >
        {artist.image ? (
          <img
            src={artist.image}
            alt={artist.name}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]">
            <div
              className="w-48 h-48 rounded-full flex items-center justify-center border-2"
              style={{
                borderColor: accentColor,
                background: `${accentColor}15`,
                boxShadow: `0 0 60px ${accentColor}30`,
              }}
            >
              <i className="ri-user-line text-7xl" style={{ color: accentColor }} />
            </div>
          </div>
        )}
        {/* より滑らかな多層グラデーション */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.15) 80%, rgba(0,0,0,0.35) 100%)`
        }} />
        <div
          className="absolute inset-0 opacity-25"
          style={{ background: `linear-gradient(to top, ${accentColor}50, transparent 50%)` }}
        />
      </div>

      {/* Text content */}
      <div className="min-h-screen flex flex-col justify-end px-6 pb-16 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-2 h-2 rounded-full"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
          <p className="text-gray-500 text-xs tracking-widest uppercase">Layer 01</p>
        </div>
        <p className="text-sm tracking-widest mb-3" style={{ color: accentColor }}>
          {artist.location}
        </p>
        {/* 強化されたタイポグラフィ */}
        <h1
          className="font-display mb-3 tracking-wider leading-none"
          style={{
            fontSize: 'clamp(3rem, 12vw, 5rem)',
            textShadow: `0 0 40px ${accentColor}90, 0 0 80px ${accentColor}40`,
            color: '#ffffff',
          }}
        >
          {artist.name}
        </h1>
        <div className="w-24 h-[2px] mb-4" style={{
          background: `linear-gradient(to right, ${accentColor}, ${accentColor}40)`,
          boxShadow: `0 0 20px ${accentColor}`
        }} />
        <p className="text-xl text-gray-300 mb-6 font-light tracking-wide">{artist.genre}</p>
        <GenreTags items={genresAndAttributes} accentColor={accentColor} />
        
        {!countLoading && (
          <FavoriteSection
            count={favoriteCount}
            accentColor={accentColor}
            artistId={artistId}
            artistType={artistType}
          />
        )}
        
        <RevenueButtons
          supportUrl={supportUrl}
          storeUrl={storeUrl}
          accentColor={accentColor}
          onResonateClick={onResonateClick}
          onFragmentClick={onFragmentClick}
        />
        {showPremiumBanner && <PremiumHintBanner />}
        <div className="flex items-center gap-2 text-gray-500 text-sm animate-bounce">
          <i className="ri-arrow-down-line" />
          <span className="tracking-wider">SCROLL</span>
        </div>
      </div>

      {showDepthPopup && (
        <DepthLevelPopup
          fanLevel={fanLevel}
          currentExp={currentExp}
          nextLevelExp={nextLevelExp}
          checkInExp={checkInExp}
          supportExp={supportExp}
          hasVeteranBonus={hasVeteranBonus}
          accentColor={accentColor}
          onClose={() => setShowDepthPopup(false)}
        />
      )}
    </>
  );
}

/** デスクトップ用ヒーロー（2カラムグリッド） */
export function ArtistHeroDesktop({
  artist,
  accentColor,
  genresAndAttributes,
  favoriteCount,
  countLoading,
  artistId,
  artistType,
  supportUrl,
  storeUrl,
  showPremiumBanner,
  onResonateClick,
  onFragmentClick,
  fanLevel = 2,
  currentExp = 26,
  nextLevelExp = 30,
  checkInExp = 15,
  supportExp = 11,
  hasVeteranBonus = false,
}: ArtistHeroProps) {
  const [showDepthPopup, setShowDepthPopup] = useState(false);

  return (
    <div className="grid lg:grid-cols-2 gap-12 mb-20">
      {/* Image */}
      <div className="relative">
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{
            border: `1px solid ${accentColor}60`,
            boxShadow: `0 0 60px ${accentColor}40, 0 0 120px ${accentColor}15`,
          }}
        >
          {artist.image ? (
            <img
              src={artist.image}
              alt={artist.name}
              className="w-full aspect-square object-cover object-top"
            />
          ) : (
            <div className="w-full aspect-square bg-[#0a0a0a] flex items-center justify-center">
              <div
                className="w-40 h-40 rounded-full flex items-center justify-center border-2"
                style={{
                  borderColor: accentColor,
                  background: `${accentColor}15`,
                  boxShadow: `0 0 40px ${accentColor}30`,
                }}
              >
                <i className="ri-user-line text-6xl" style={{ color: accentColor }} />
              </div>
            </div>
          )}
          {/* より滑らかなグラデーションオーバーレイ */}
          <div className="absolute inset-0" style={{
            background: `linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 40%, transparent 70%)`
          }} />
          {/* アクセントカラーの微光 */}
          <div className="absolute inset-0 opacity-10" style={{
            background: `radial-gradient(ellipse at bottom, ${accentColor}, transparent 70%)`
          }} />
        </div>
        <div
          className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full blur-3xl opacity-40"
          style={{ background: accentColor }}
        />
      </div>

      {/* Info */}
      <div className="flex flex-col justify-center">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
            />
            <p className="text-gray-500 text-xs tracking-widest uppercase">Layer 01</p>
          </div>
          <p className="text-sm tracking-widest mb-4" style={{ color: accentColor }}>
            {artist.location}
          </p>
          {/* 強化されたタイポグラフィ */}
          <h1
            className="font-display mb-6 tracking-wider leading-none"
            style={{
              fontSize: 'clamp(3.5rem, 6vw, 6rem)',
              textShadow: `0 0 50px ${accentColor}80, 0 0 100px ${accentColor}30`,
              color: '#ffffff',
            }}
          >
            {artist.name}
          </h1>
          <div className="w-32 h-[2px] mb-8" style={{
            background: `linear-gradient(to right, ${accentColor}, ${accentColor}30)`,
            boxShadow: `0 0 20px ${accentColor}`
          }} />
          <p className="text-2xl text-gray-300 mb-8 font-light tracking-wide">{artist.genre}</p>
          <GenreTags items={genresAndAttributes} accentColor={accentColor} />

          {!countLoading && (
            <FavoriteSection
              count={favoriteCount}
              accentColor={accentColor}
              artistId={artistId}
              artistType={artistType}
            />
          )}

          <RevenueButtons
            supportUrl={supportUrl}
            storeUrl={storeUrl}
            accentColor={accentColor}
            onResonateClick={onResonateClick}
            onFragmentClick={onFragmentClick}
          />
          {showPremiumBanner && <PremiumHintBanner />}
        </div>
      </div>

      {showDepthPopup && (
        <DepthLevelPopup
          fanLevel={fanLevel}
          currentExp={currentExp}
          nextLevelExp={nextLevelExp}
          checkInExp={checkInExp}
          supportExp={supportExp}
          hasVeteranBonus={hasVeteranBonus}
          accentColor={accentColor}
          onClose={() => setShowDepthPopup(false)}
        />
      )}
    </div>
  );
}
